import React, { FC, useMemo, useState } from 'react';

import styled from 'styled-components';

// Constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

// Models
import { DatasetFiltersType } from '../../../../../models/dataset-filters.model';
import { ElementKind } from '../../../../../models/editor.model';
import { DatasetModel } from 'models/dataset.model';
import { TestFormulaVariantData, VariantWithLabel } from 'models/variant.model';

// Components
import TestFormulaElementVariantTable from './TestFormulaElementVariantTable';
import TestFormulaDatasetSelectionModal from './elements/TestFormulaDatasetSelectionModal';

interface TestFormulaElementFieldProps {
    icon: React.ReactNode;
    type: ElementKind;
    name: string;
    elementId: string;
    data?: TestFormulaVariantData[] | DatasetModel;
    selectedElement?: VariantWithLabel | DatasetFiltersType;
}

const TestFormulaElementField: FC<TestFormulaElementFieldProps> = ({
    icon,
    type,
    name,
    elementId,
    data,
    selectedElement,
}) => {
    const placeholderString =
        type === ElementKind.dataset ? 'Select a row' : 'Select an alternate';

    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const selectedValueText = useMemo<string>(() => {
        if (type === ElementKind.variant && selectedElement) {
            return (selectedElement as VariantWithLabel).content;
        } else if (type === ElementKind.dataset && selectedElement) {
            return '[' + Object.values(selectedElement).join(', ') + ']';
        } else if (type === ElementKind.image || type === ElementKind.section) {
            return '';
        } else {
            return placeholderString;
        }
    }, [placeholderString, selectedElement, type]);

    return (
        <DropdownContainer key={elementId}>
            <Heading onClick={() => setIsExpanded((prev) => !prev)}>
                <LeftContent>
                    <IconWrapper>{icon}</IconWrapper>
                    <TypeText>{name}</TypeText>
                    <SelectedValueText>{selectedValueText}</SelectedValueText>
                </LeftContent>
                {isExpanded ? <DownArrowIcon /> : <RightArrowIcon />}
            </Heading>
            {isExpanded && type !== ElementKind.dataset && data && (
                <TestFormulaElementVariantTable
                    alternateVariants={data as TestFormulaVariantData[]}
                    setIsExpanded={setIsExpanded}
                    selectedVariant={selectedElement as VariantWithLabel}
                    type={type}
                />
            )}
            {isExpanded && type === ElementKind.dataset && (
                <TestFormulaDatasetSelectionModal
                    datasetId={Number(elementId)}
                    setIsExpanded={setIsExpanded}
                    selectedDataset={selectedElement as DatasetFiltersType}
                />
            )}
        </DropdownContainer>
    );
};

export default TestFormulaElementField;

const DropdownContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.medium,
    width: '550px',
});

const Heading = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: aampeTheme.spacing4(3, 4),
    borderRadius: aampeTheme.borderRadius.medium,
    cursor: 'pointer',
    width: '100%',
});

const LeftContent = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(3),
    overflow: 'hidden',
});

const IconWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const TypeText = styled.span({
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
    whiteSpace: 'nowrap',
});

const SelectedValueText = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
});

const commonStyle = {
    cursor: 'pointer',
    color: aampeTheme.colors.solidA01,
    height: '20px',
    width: '20px',
};

const RightArrowIcon = styled(ICONS.rightChevron)(() => commonStyle);
const DownArrowIcon = styled(ICONS.arrowDown)(() => commonStyle);
